import { Layers, Play, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CartSnapshot } from '@/store/cartStore';
import { formatMoney } from '@/utils/currency';

interface Props {
  holds: CartSnapshot[];
  pricesIncludeTax?: boolean;
  holdTotal: (snap: CartSnapshot) => number;
  formatTime: (iso: string) => string;
  onResume: (id: string) => void;
  onDiscard: (id: string) => void;
}

export default function HeldCartsPanel({
  holds,
  holdTotal,
  formatTime,
  onResume,
  onDiscard,
}: Props) {
  const { t } = useTranslation();

  return (
    <div className="mt-3 border border-amber-200 rounded-xl bg-amber-50/40 divide-y divide-amber-100 max-h-[min(24rem,50vh)] overflow-auto">
      {holds.map((h) => (
        <div key={h.id} className="p-3 space-y-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{h.label}</p>
            <p className="text-xs text-gray-600 mt-0.5">
              {t('pos.heldLineSummary', {
                count: h.items.length,
                total: formatMoney(holdTotal(h)),
                time: formatTime(h.heldAt),
              })}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => onResume(h.id)}
              className="pos-held-action pos-held-action-resume inline-flex items-center justify-center gap-2 min-h-[48px] px-3 rounded-xl text-base font-semibold bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 transition-colors"
            >
              <Play className="w-4 h-4 shrink-0" />
              {t('pos.resume')}
            </button>
            <button
              type="button"
              onClick={() => onDiscard(h.id)}
              className="pos-held-action pos-held-action-discard inline-flex items-center justify-center gap-2 min-h-[48px] px-3 rounded-xl text-base font-semibold border-2 border-red-200 bg-white text-red-700 hover:bg-red-50 active:bg-red-100 transition-colors"
            >
              <Trash2 className="w-4 h-4 shrink-0" />
              {t('pos.discard')}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Prominent touch-friendly toggle for held carts list. */
export function HeldCartsToggleButton({
  count,
  active,
  shortcutBinding,
  onClick,
  className = '',
}: {
  count: number;
  active?: boolean;
  shortcutBinding?: string;
  onClick: () => void;
  className?: string;
}) {
  const { t } = useTranslation();

  if (count <= 0) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      title={shortcutBinding ? `${t('pos.held')} (${shortcutBinding})` : t('pos.held')}
      className={`pos-held-toggle relative inline-flex items-center gap-2 min-h-[44px] px-3 py-2 rounded-xl text-sm font-semibold border transition-colors ${
        active
          ? 'bg-amber-100 text-amber-900 border-amber-300'
          : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
      } ${className}`}
    >
      <Layers className="w-4 h-4 shrink-0" />
      <span>{t('pos.held')}</span>
      <span className="inline-flex items-center justify-center min-w-[1.35rem] h-5 px-1.5 rounded-full bg-amber-600 text-white text-[11px] font-bold">
        {count}
      </span>
    </button>
  );
}
